// Overview
// Dependencies Array:
// In the options for a step, we include dependencies: [step1, step2]. This indicates that before executing the current step's redirect logic, all listed dependencies must successfully execute their redirect logic.

import useLoading from "@/composables/useLoading";
import { navigateToError } from "../navigate";
import { CatalogManagerApi } from "@/service/CatalogApiService/CatalogManagerApi";
import store from "@/store/BookingStore";
import { handleRetroBo } from "@/utils/hanldeRetroBo";

// redirectWithDependencies() Method:
// This method iterates over any dependencies, invoking each dependency’s redirect method. If any dependency returns a redirect result, it halts and returns that result. Otherwise, it proceeds to execute the current step’s redirect method.

// run() Method:
// The run() method first executes the step’s initialization logic, then calls redirectWithDependencies. If any step (including dependencies) returns a redirect result, it returns that result.

// Extensibility:
// This design allows for easy addition of further dependencies or additional logic within each step’s redirect method. For example, if step3 should depend on both step1 and step2, you would simply list both in its dependencies array.

export interface FunnelStepOptions {
    name: string;
    dependencies?: FunnelStep[];
    init?: () => Promise<false | string>;
    hasToBeLoggedIn: boolean
}

export class FunnelStep {
    name: string;
    dependencies?: FunnelStep[];
    init?: () => Promise<false | string>;
    hasToBeLoggedIn: boolean

    constructor(options: FunnelStepOptions) {
        this.name = options.name;
        this.dependencies = options.dependencies;
        this.init = options.init;
        this.hasToBeLoggedIn = options.hasToBeLoggedIn
    }

    /**
     * Executes initialization and redirect logic. If any step (including dependencies) requires redirection, returns the redirect result.
     */
    async run(): Promise<false | string> {
        const { startLoadingThis, stopLoadingThis } = useLoading('funnel-step')
        try {
            startLoadingThis()
            let redirectResults: false | string = false
            // handle retro compatibility with old funnel
            if (store.getters.getToken?.includes('BO')) {
                console.log('handle retro compatibility with old funnel')
                await handleRetroBo(store.getters.getToken)
                return;
            }
            if (this.init) {
                redirectResults = await this.init();
            }
            if (store.getters.getBooking?.liheId && !store.getters.getProduct) {
                const catalogManagerApi = new CatalogManagerApi(store.getters.getLocale)
                const response = await catalogManagerApi.getLihe(store.getters.getBooking.liheId)
                store.commit('setProduct', response.message.lihe)
            }
            stopLoadingThis()
            return redirectResults;
        } catch (error) {
            navigateToError()
        } finally {
            stopLoadingThis()
        }

    }
}